import React, { useState, useEffect, lazy, Suspense } from 'react';
import useGameStore from '../../store/gameStore';
import { Sparkles, Trash2, Save, Box, Grid3X3, Lock } from 'lucide-react';
import { toast } from '../ui/sonner';
import { formatMoney } from '../../utils/format';
import {
  NIVEAUX, nbColonnes, slotKey, computeFacing, suggererFacing, famillesActives, coutFacing, MAX_PROMO_FACING,
  merchSecteur, typeMagasinSecteur,
} from '../../utils/merchandising';
import { getTypeMagasin } from '../../utils/ouverture';
import { verrousSecteur, forcerVerrous } from '../../utils/facingNegocie';
import { OffresFacingPanel } from './OffresFacingPanel';
import { PaletteMerch, couleurItem, libelleItem } from './PaletteMerch';
import { ScoreMerch } from './ScoreMerch';

const Rayon3D = lazy(() => import('./Rayon3D'));

const ZONES_FACING = [{ id: 'promo', nom: 'Facing promo', emoji: '🏷️', description: `Max ${MAX_PROMO_FACING} : +1 pt de conversion chacun.` }];

/** Planogramme interactif : 4 niveaux × N colonnes selon le format du magasin. */
export const PlanogrammeEditor = ({ secteurId: secteurProp = null }) => {
  const retail = useGameStore(s => s.retail);
  const treasury = useGameStore(s => s.treasury) || 0;
  const month = useGameStore(s => s.month) || 1;
  const appliquerFacing = useGameStore(s => s.appliquerFacing);
  const secteurId = secteurProp || retail.secteur;
  const fams = famillesActives(retail, secteurId);
  const typeId = typeMagasinSecteur(retail, secteurId);
  const cols = nbColonnes(typeId);
  const cfg = merchSecteur(retail, secteurId);
  const opts = { secteurId, month };
  const verrous = verrousSecteur(retail, secteurId);
  const [draft, setDraftBrut] = useState(() => forcerVerrous(retail, secteurId, cfg.facing?.slots || {}));
  const setDraft = (next) => setDraftBrut(d => forcerVerrous(retail, secteurId, typeof next === 'function' ? next(d) : next));
  const [brush, setBrush] = useState(null);
  const [vue3d, setVue3d] = useState(true);
  const slotsAppliques = cfg.facing?.slots;
  useEffect(() => { if (slotsAppliques) setDraftBrut(forcerVerrous(retail, secteurId, slotsAppliques)); }, [slotsAppliques]); // eslint-disable-line react-hooks/exhaustive-deps

  const fx = computeFacing(retail, draft, opts);
  const dejaConfigure = !!cfg.facing;
  const cout = dejaConfigure ? coutFacing(typeId) : 0;
  const modifie = JSON.stringify(draft) !== JSON.stringify(cfg.facing?.slots || {});

  const poser = (key, value) => { if (verrous[key]) { toast('Emplacement réservé par un contrat fournisseur'); return; } setDraft(d => ({ ...d, [key]: value === '__vide' ? null : value })); };
  const onClickSlot = (key) => { if (brush) poser(key, brush); };
  const onDrop = (key, e) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('text/plain');
    if (data.startsWith('move:')) {
      const from = data.slice(5);
      if (from === key || verrous[key] || verrous[from]) return;
      setDraft(d => ({ ...d, [key]: d[from] || null, [from]: d[key] || null }));
    } else if (data) poser(key, data);
  };

  const appliquer = () => {
    const res = appliquerFacing(draft, secteurId);
    if (res?.ok) toast('Planogramme appliqué', { description: `Score ${res.score}/100 · ${res.cout ? `${formatMoney(res.cout)} de manutention` : 'mise en place offerte'}. Effets dès demain.` });
    else if (res?.reason === 'fonds') toast.error('Trésorerie insuffisante pour la manutention.');
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4" data-testid="planogramme-editor">
      <div className="space-y-4">
        <OffresFacingPanel secteurId={secteurId} />

        <PaletteMerch fams={fams} zones={ZONES_FACING} selection={brush} onSelect={setBrush} testPrefix="facing" />

        <div className="flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-widest text-slate-500">{vue3d ? 'Rayon 3D' : 'Grille du planogramme'}</p>
          <button type="button" onClick={() => setVue3d(v => !v)} className="px-2.5 py-1 rounded text-[11px] font-bold border border-ink-600 text-slate-300 hover:bg-ink-800 flex items-center gap-1.5" data-testid="facing-toggle-3d">
            {vue3d ? <><Grid3X3 className="w-3.5 h-3.5" /> Masquer la 3D</> : <><Box className="w-3.5 h-3.5" /> Afficher le rayon 3D</>}
          </button>
        </div>
        {vue3d && (
          <Suspense fallback={<div className="h-[340px] rounded-lg border border-ink-600 bg-ink-950 flex items-center justify-center text-xs text-slate-500" data-testid="rayon-3d-loading">Chargement du rayon 3D…</div>}>
            <Rayon3D slots={draft} cols={cols} fams={fams} secteurId={secteurId} libelle={(v) => libelleItem(v, fams)} onSlotClick={onClickSlot} onSlotClear={(key) => poser(key, null)} />
          </Suspense>
        )}

        <div className="rounded-lg border border-ink-600 bg-ink-900/60 p-3 overflow-x-auto" data-testid="planogramme-grille">
          <div className="min-w-[520px] space-y-1">
            {NIVEAUX.map(n => (
              <div key={n.id} className="grid items-stretch gap-2" style={{ gridTemplateColumns: `130px repeat(${cols}, minmax(0, 1fr))` }} data-testid={`facing-niveau-${n.id}`}>
                <div className="flex flex-col justify-center pr-2" title={n.description}>
                  <p className="text-[11px] font-bold text-slate-200 leading-tight">{n.nom}</p>
                  <p className="text-[9px] font-mono text-slate-500">visibilité ×{n.coef}</p>
                </div>
                {Array.from({ length: cols }, (_, c) => {
                  const key = slotKey(n.id, c);
                  const v = draft[key] || null;
                  return (
                    <div
                      key={key}
                      role="button"
                      tabIndex={0}
                      draggable={!!v && !verrous[key]}
                      onDragStart={e => { e.dataTransfer.setData('text/plain', `move:${key}`); }}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => onDrop(key, e)}
                      onClick={() => onClickSlot(key)}
                      onKeyDown={e => { if (e.key === 'Enter') onClickSlot(key); }}
                      onContextMenu={e => { e.preventDefault(); poser(key, null); }}
                      title={libelleItem(v, fams)}
                      className={`relative h-14 rounded-t-md border-b-4 flex items-end ${verrous[key] ? 'border-amber-400 ring-1 ring-amber-400/60' : 'border-ink-500'} justify-center px-1 pb-1 text-[9px] font-bold text-white text-center leading-tight select-none transition-transform hover:scale-[1.03] cursor-pointer ${couleurItem(v, fams)} ${!v ? 'border-dashed border border-ink-600 text-slate-600' : ''} ${brush ? 'cursor-crosshair' : ''}`}
                      data-testid={`facing-slot-${key}`}
                      data-value={v || ''}
                      data-locked={verrous[key] ? 'true' : undefined}
                    >
                      {verrous[key] && <Lock className="absolute top-1 right-1 w-3 h-3 text-amber-300" data-testid={`facing-lock-${key}`} />}
                      <span className="line-clamp-2">{v ? libelleItem(v, fams) : '·'}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 mt-2">Clic droit sur un emplacement pour le vider · glissez un emplacement sur un autre pour les échanger. {cols} facings par niveau ({getTypeMagasin(typeId)?.nom || 'boutique'}) · demande du mois {month}.</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setDraft(suggererFacing(retail, opts))} className="px-3 py-2 rounded text-xs font-bold border border-accent/60 text-accent hover:bg-accent/10 flex items-center gap-1.5" data-testid="facing-suggestion">
            <Sparkles className="w-3.5 h-3.5" /> Suggestion automatique
          </button>
          <button type="button" onClick={() => setDraft({})} className="px-3 py-2 rounded text-xs font-bold border border-ink-600 text-slate-300 hover:bg-ink-800 flex items-center gap-1.5" data-testid="facing-vider">
            <Trash2 className="w-3.5 h-3.5" /> Tout vider
          </button>
          <button
            type="button"
            onClick={appliquer}
            disabled={!modifie || treasury < cout}
            className="ml-auto px-4 py-2 rounded text-xs font-bold bg-accent text-white hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            data-testid="facing-appliquer"
          >
            <Save className="w-3.5 h-3.5" /> Appliquer {cout ? `(${formatMoney(cout)} de manutention)` : '(mise en place offerte)'}
          </button>
        </div>

        {fx.configure && (
          <div className="surface-card p-3 overflow-x-auto" data-testid="facing-details">
            <table className="w-full text-[11px]">
              <thead className="text-[9px] uppercase tracking-wider text-slate-500">
                <tr><th className="text-left py-1">Famille</th><th className="text-right">Facings</th><th className="text-right">Visibilité</th><th className="text-right">Demande</th><th className="text-right">Effet ventes</th></tr>
              </thead>
              <tbody>
                {fx.details.map(d => (
                  <tr key={d.famId} className="border-t border-ink-800" data-testid={`facing-detail-${d.famId}`}>
                    <td className="py-1 text-slate-200 font-bold flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-sm ${couleurItem(d.famId, fams)}`} />{d.nom}</td>
                    <td className="text-right font-mono text-slate-300">{d.facings}</td>
                    <td className="text-right font-mono text-slate-300">{Math.round(d.visibShare * 100)} %</td>
                    <td className="text-right font-mono text-slate-400">{Math.round(d.demandeShare * 100)} %</td>
                    <td className={`text-right font-mono font-bold ${fx.familleMult[d.famId] > 1 ? 'text-emerald-400' : fx.familleMult[d.famId] < 1 ? 'text-red-400' : 'text-slate-300'}`}>×{fx.familleMult[d.famId].toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ScoreMerch
        titre="Score planogramme"
        fx={fx}
        testPrefix="facing"
        effets={[
          { key: 'conversion', label: 'Conversion', value: fx.conversionPct },
          { key: 'panier', label: 'Panier moyen', value: fx.panierPct },
          { key: 'yeux', label: 'Marge aux yeux', value: Math.round((fx.yeuxMarge || 0) * 100) },
        ]}
      />
    </div>
  );
};
